import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { OfflineSyncService } from '../../../Services/offline/offline-sync.service';
import { SyncKonfliktEintrag, SyncQueueEintrag, SyncTyp } from '../../../Models/offline.model';

/**
 * Zwei Leute haben denselben Prüfpunkt bearbeitet – wer gewinnt?
 *
 * Julia lädt Prüfpunkt Stand 4 herunter und fährt auf die Anlage. Max ändert
 * ihn online, der Server steht danach auf Stand 5. Julia synchronisiert
 * abends ihre Fassung, die noch auf Stand 4 beruht.
 *
 * Der Server hat sie deshalb NICHT angewendet, sondern gemeldet. Hier
 * entscheidet ein Mensch, welche Fassung gilt. „Last write wins" wäre die
 * bequemere Lösung und würde regelmäßig die Arbeit von jemandem vernichten,
 * ohne dass es irgendwo auffällt.
 *
 * Die Oberfläche ist bewusst schlicht: zwei Werte nebeneinander, zwei Knöpfe.
 * Was zählt, ist, dass nichts still überschrieben wird.
 */
@Component({
  selector: 'app-sync-konflikt-dialog',
  templateUrl: './sync-konflikt-dialog.component.html',
  styleUrls: ['./sync-konflikt-dialog.component.css']
})
export class SyncKonfliktDialogComponent implements OnInit {

  konflikte: SyncKonfliktEintrag[] = [];

  /**
   * Änderungen, die der Server endgültig abgelehnt hat – fachlich falsch,
   * ohne Berechtigung oder auf einen inzwischen gelöschten Datensatz. Sie
   * bleiben sichtbar, damit der Benutzer weiß, was NICHT angekommen ist.
   */
  fehlgeschlagen: SyncQueueEintrag[] = [];

  isLoading = true;

  /** Konflikte, die gerade aufgelöst werden – gegen Doppelklicks. */
  laufend = new Set<string>();

  constructor(
    private sync: OfflineSyncService,
    private dialogRef: MatDialogRef<SyncKonfliktDialogComponent>
  ) { }

  ngOnInit(): void {
    void this.laden();
  }

  private async laden(): Promise<void> {
    this.isLoading = true;
    this.konflikte = await this.sync.ladeKonflikte();
    this.fehlgeschlagen = await this.sync.ladeFehlgeschlagene();
    this.isLoading = false;

    if (this.konflikte.length === 0 && this.fehlgeschlagen.length === 0) {
      this.dialogRef.close();
    }
  }

  /**
   * Verwirft die endgültig abgelehnten Änderungen.
   *
   * Ausdrücklich vom Benutzer angestoßen. Automatisch verschwindet hier
   * nichts – eine Prüfung stillschweigend zu löschen, weil der Server sie
   * nicht mochte, wäre genau der Datenverlust, den diese Funktion verhindern
   * soll.
   */
  async verwerfeFehlgeschlagene(): Promise<void> {
    const anzahl = this.fehlgeschlagen.length;

    if (anzahl === 0) {
      return;
    }

    const frage =
      anzahl === 1
        ? 'Diese Änderung endgültig verwerfen? Sie wird nicht mehr übertragen.'
        : `Diese ${anzahl} Änderungen endgültig verwerfen? Sie werden nicht mehr übertragen.`;

    if (!confirm(frage)) {
      return;
    }

    await this.sync.verwerfeFehlgeschlagene();
    await this.laden();
  }

  /** Lesbare Bezeichnung einer Änderungsart. */
  typText(typ: SyncTyp): string {
    switch (typ) {
      case 'PRUEFPUNKT_ERGEBNIS':
        return 'Prüfergebnis';

      case 'PRUEFPUNKT_ANLEGEN':
        return 'Prüfpunkt anlegen';

      case 'PRUEFPUNKT_LOESCHEN':
        return 'Prüfpunkt löschen';

      case 'MESSSTELLE_PRUEFERGEBNIS':
        return 'Gesamtergebnis';

      // Meldet selbst keine Konflikte; der Vollständigkeit halber beschriftet.
      case 'MESSSTELLE_NOTIZ':
        return 'Notiz zur Messstelle';

      case 'MESSSTELLE_SIGNALSTATUS':
        return 'Signalstatus';

      case 'MESSSTELLE_KATEGORIE':
        return 'Kategorie';

      case 'MESSSTELLE_WERTE':
        return 'Allgemeine Daten';

      default:
        return typ;
    }
  }

  trackByFehler(_index: number, eintrag: SyncQueueEintrag): string {
    return eintrag.id;
  }

  /** Die Fassung des Servers gilt; die eigene Änderung wird verworfen. */
  async serverfassung(konflikt: SyncKonfliktEintrag): Promise<void> {
    if (this.laufend.has(konflikt.id)) {
      return;
    }

    this.laufend.add(konflikt.id);

    await this.sync.uebernehmeServerfassung(konflikt.id);

    this.laufend.delete(konflikt.id);

    await this.laden();
  }

  /**
   * Die eigene, offline erfasste Fassung gilt.
   *
   * Sie geht erneut hinaus – diesmal mit dem Stand des Servers als Basis,
   * sodass er sie durchlässt. Das ist kein Trick: der Benutzer hat die
   * Serverfassung gesehen und sich bewusst dagegen entschieden.
   */
  async lokaleFassung(konflikt: SyncKonfliktEintrag): Promise<void> {
    if (this.laufend.has(konflikt.id)) {
      return;
    }

    this.laufend.add(konflikt.id);

    await this.sync.uebernehmeLokaleFassung(konflikt.id);

    this.laufend.delete(konflikt.id);

    await this.laden();
  }

  schliessen(): void {
    this.dialogRef.close();
  }

  trackByKonflikt(_index: number, konflikt: SyncKonfliktEintrag): string {
    return konflikt.id;
  }
}
